
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function SubjectsSection({ token, setError }) {
  const [subjects, setSubjects] = useState(null);
  const [newSubject, setNewSubject] = useState('');
  const [newAcronym, setNewAcronym] = useState('');

  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add','edit','delete'
  const [selected, setSelected] = useState(null); // subject for edit/delete


  const fetch = async () => {
    try {
      const res = await api.get('/admin/subjects', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(res.data.subjects);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch subjects');
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch();
  }, [token]);

  if (!subjects) return null;

  return (
    <div className="mb-6">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur border-b border-gray-100 py-3 px-1 rounded-t-lg flex justify-between items-center mb-4 shadow-sm">
        <h3 className="text-2xl font-bold text-indigo-700 tracking-tight">Subjects</h3>
        <button
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
          onClick={() => { setModalMode('add'); setSelected(null); setNewSubject(''); setNewAcronym(''); setModalOpen(true); }}
          aria-label="Add new subject"
        >
          + Add Subject
        </button>
      </div>

      {/* modal overlay */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md border border-gray-100">
            {modalMode==='delete' ? (
              <>
                <h4 className="text-lg font-semibold mb-4 text-red-600">Delete Subject</h4>
                <p className="mb-4">Do you really want to delete <span className="font-semibold">{selected?.name}</span>?</p>
                <div className="flex justify-end space-x-2">
                  <button className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300" onClick={()=>{setModalOpen(false);setSelected(null);}}>Cancel</button>
                  <button className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600" onClick={async()=>{
                    try{await api.delete(`/admin/subjects/${selected.id}`,{headers:{Authorization:`Bearer ${token}`}});fetch();}catch(e){setError(e.response?.data?.error||'');}
                    setModalOpen(false);setSelected(null);
                  }}>Delete</button>
                </div>
              </>
            ) : (
              <>
                <h4 className="text-lg font-semibold mb-2">{modalMode==='edit'?'Edit':'Add'} Subject</h4>
                <div className="space-y-2">
                  <input
                    placeholder="Subject name"
                    value={selected?selected.name:newSubject}
                    onChange={e=>{
                      if(selected) setSelected({...selected,name:e.target.value});
                      else setNewSubject(e.target.value);
                    }}
                    className="border rounded px-2 w-full focus:ring-2 focus:ring-indigo-200"
                    aria-label="Subject name"
                  />
                  <input
                    placeholder="Acronym (optional)"
                    value={selected?selected.acronym:newAcronym}
                    onChange={e=>{
                      if(selected) setSelected({...selected,acronym:e.target.value});
                      else setNewAcronym(e.target.value);
                    }}
                    className="border rounded px-2 w-full focus:ring-2 focus:ring-indigo-200"
                    aria-label="Acronym"
                  />
                </div>
                <div className="flex justify-between space-x-2 mt-4">
                  <button className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300" onClick={()=>setModalOpen(false)}>Cancel</button>
                  <button className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={async()=>{
                    try{
                      if(modalMode==='edit'){
                        await api.put(`/admin/subjects/${selected.id}`,{name:selected.name,acronym:selected.acronym},{headers:{Authorization:`Bearer ${token}`}});
                      } else {
                        await api.post('/admin/subjects',{name:newSubject,acronym:newAcronym},{headers:{Authorization:`Bearer ${token}`}});
                        setNewSubject('');setNewAcronym('');
                      }
                      fetch();
                    }catch(e){setError(e.response?.data?.error||'');}
                    setModalOpen(false);
                    setSelected(null);
                  }}>{modalMode==='edit'?'Update':'Add'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {subjects.length === 0 ? (
        <div className="py-16 text-center text-gray-500 bg-white rounded-lg shadow-inner mt-8">
          <div className="text-2xl mb-2">No subjects found</div>
          <div className="mb-4">Get started by adding your first subject.</div>
          <button
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2 rounded-lg shadow focus:outline-none focus:ring-2 focus:ring-emerald-300 transition"
            onClick={() => { setModalMode('add'); setSelected(null); setNewSubject(''); setNewAcronym(''); setModalOpen(true); }}
            aria-label="Add new subject"
          >
            + Add Subject
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {subjects.map((s) => (
            <div
              key={s.id}
              className="group p-4 bg-white rounded-xl border border-slate-200 shadow hover:shadow-xl hover:-translate-y-1 transition-all duration-200 flex justify-between items-center focus-within:ring-2 focus-within:ring-indigo-300 outline-none"
              tabIndex={0}
              aria-label={`Subject ${s.name}`}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium text-slate-800 text-lg truncate">
                  {s.name} {s.acronym && <span className="text-xs text-gray-500">({s.acronym})</span>}
                </span>
              </div>
              <div className="space-x-2 flex-shrink-0 flex items-center">
                <button
                  className="text-indigo-600 hover:text-indigo-800 text-sm font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  onClick={() => {
                    navigate(`/subjects/${s.id}/topics`);
                  }}
                  aria-label={`View topics for ${s.name}`}
                >
                  Topics
                </button>
                <button
                  className="text-blue-500 hover:text-blue-700 text-sm font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-200"
                  onClick={() => {
                    setModalMode('edit');
                    setSelected({ id: s.id, name: s.name, acronym: s.acronym });
                    setModalOpen(true);
                  }}
                  aria-label={`Edit subject ${s.name}`}
                >
                  Edit
                </button>
                <button
                  className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-red-200"
                  onClick={() => {
                    setModalMode('delete');
                    setSelected({ id: s.id, name: s.name });
                    setModalOpen(true);
                  }}
                  aria-label={`Delete subject ${s.name}`}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
